import { supabase } from "@/lib/supabase";
import { asJson } from "@/lib/checklistJson";
import { insertCleaningPropertyIssue } from "@/lib/cleaningIssueApi";
import { getSafeFileName } from "@/lib/utils";
import { safeUpdateChecklist } from "@/lib/safeUpdateChecklist";
import { deleteMutation, deletePhotoBlob, getAllMutations, getPhotoBlob, putMutation } from "@/lib/offline/offlineDb";
import { enqueueIssue } from "@/lib/offline/offlineQueue";
import type {
  CreateIssuePayload,
  TaskNotePayload,
  TaskPhotoPayload,
  ToggleTaskPayload,
} from "@/lib/offline/offlineTypes";

const TASK_PHOTO_BUCKET = "photos";
const ISSUE_BUCKET = "property-issues";
const TEMP_UPLOADS_PREFIX = "temp_uploads";

let syncInFlight: Promise<number> | null = null;

async function applyToggle(payload: ToggleTaskPayload): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error: fetchError } = await supabase
    .from("cleaning_tasks")
    .select("checklist")
    .eq("id", payload.originTaskId)
    .maybeSingle();
  if (fetchError) throw fetchError;

  const updates: Record<string, unknown> = { completed: payload.completed };
  if (payload.completed && user?.id) {
    updates.metadata = { completed_by: user.id };
  }
  const updatedChecklist = safeUpdateChecklist(data?.checklist, payload.stepId, updates, {
    fallbackName: payload.stepName,
  });

  const updatePayload: Record<string, unknown> = {
    checklist: asJson(updatedChecklist),
    status: payload.completed ? "done" : "pending",
    completed_at: payload.completed ? new Date().toISOString() : null,
    actual_performer_id: payload.completed ? user?.id ?? null : null,
  };

  const { error } = await supabase
    .from("cleaning_tasks")
    .update(updatePayload)
    .eq("id", payload.originTaskId);
  if (error) throw error;

  if (payload.completed) {
    await supabase.from("task_step_logs").insert({
      task_id: payload.originTaskId,
      step_name: payload.stepName,
      user_id: user?.id,
      is_extra_task: payload.isExtraTask ?? false,
    });
  } else {
    await supabase
      .from("task_step_logs")
      .delete()
      .eq("task_id", payload.originTaskId)
      .eq("step_name", payload.stepName);
  }
}

async function applyTaskNote(payload: TaskNotePayload): Promise<void> {
  const { data, error: fetchError } = await supabase
    .from("cleaning_tasks")
    .select("checklist")
    .eq("id", payload.originTaskId)
    .maybeSingle();
  if (fetchError) throw fetchError;

  const updatedChecklist = safeUpdateChecklist(data?.checklist, payload.stepId, { note: payload.note }, {
    fallbackName: payload.stepName,
  });
  const { error } = await supabase
    .from("cleaning_tasks")
    .update({ checklist: asJson(updatedChecklist) })
    .eq("id", payload.originTaskId)
    .eq("org_id", payload.orgId);
  if (error) throw error;
}

async function applyTaskPhoto(payload: TaskPhotoPayload, photoBlobId: string): Promise<void> {
  const blob = await getPhotoBlob(photoBlobId);
  if (!blob) throw new Error("Brak zdjęcia offline do wysłania");

  const file = new File([blob], payload.fileName || "photo.jpg", { type: blob.type || "image/jpeg" });
  const safeName = getSafeFileName(file, "photo");
  const fileName = `${payload.originTaskId}/${Date.now()}-${safeName}`;

  const { error: uploadError } = await supabase.storage.from(TASK_PHOTO_BUCKET).upload(fileName, file);
  if (uploadError) throw uploadError;

  const { data: { publicUrl } } = supabase.storage.from(TASK_PHOTO_BUCKET).getPublicUrl(fileName);

  const { data, error: fetchError } = await supabase
    .from("cleaning_tasks")
    .select("checklist")
    .eq("id", payload.originTaskId)
    .maybeSingle();
  if (fetchError) throw fetchError;

  const current = data?.checklist;
  let currentUrls: string[] = [];
  if (Array.isArray(current)) {
    const item = current.find((row) => {
      if (!row || typeof row !== "object") return false;
      const rec = row as { id?: string; step_id?: string; name?: string };
      return rec.id === payload.stepId || rec.step_id === payload.stepId || rec.name === payload.stepName;
    }) as { photo_urls?: string[] } | undefined;
    currentUrls = item?.photo_urls ?? [];
  }

  const updatedChecklist = safeUpdateChecklist(
    current,
    payload.stepId,
    { photo_urls: [...currentUrls, publicUrl], photo_required: false },
    { fallbackName: payload.stepName },
  );

  const { error } = await supabase
    .from("cleaning_tasks")
    .update({ checklist: asJson(updatedChecklist) })
    .eq("id", payload.originTaskId);
  if (error) throw error;

  await deletePhotoBlob(photoBlobId);
}

async function applyIssue(payload: CreateIssuePayload, photoBlobId?: string): Promise<void> {
  let photoUrl: string | null = null;
  if (photoBlobId) {
    const blob = await getPhotoBlob(photoBlobId);
    if (blob) {
      const file = new File([blob], "issue.jpg", { type: blob.type || "image/jpeg" });
      const filePath = `${TEMP_UPLOADS_PREFIX}/${payload.locationId}/${Date.now()}-${Math.random().toString(36).slice(2, 9)}.jpg`;
      const { error: uploadError } = await supabase.storage.from(ISSUE_BUCKET).upload(filePath, file, {
        cacheControl: "3600",
        upsert: true,
      });
      if (uploadError) throw uploadError;
      photoUrl = supabase.storage.from(ISSUE_BUCKET).getPublicUrl(filePath).data.publicUrl || null;
      await deletePhotoBlob(photoBlobId);
    }
  }

  const { data: locationData } = await supabase
    .from("cleaning_locations")
    .select("auto_notify_issues")
    .eq("id", payload.locationId)
    .maybeSingle();

  await insertCleaningPropertyIssue({
    description: payload.description,
    locationId: payload.locationId,
    orgId: payload.orgId,
    reporterId: payload.reporterId,
    photoUrl,
    autoNotify: locationData?.auto_notify_issues === true,
  });
}

async function migrateLegacyPendingIssues(): Promise<void> {
  if (typeof localStorage === "undefined") return;
  const raw = localStorage.getItem("pending_issues");
  if (!raw) return;
  try {
    const pending = JSON.parse(raw) as Array<{
      description: string;
      locationId: string;
      orgId: string;
      reporterId: string;
      sectionName?: string;
    }>;
    for (const issue of pending) {
      if (!issue?.description || !issue.locationId || !issue.orgId || !issue.reporterId) continue;
      await enqueueIssue({
        description: issue.description,
        locationId: issue.locationId,
        orgId: issue.orgId,
        reporterId: issue.reporterId,
        sectionName: issue.sectionName,
      });
    }
    localStorage.removeItem("pending_issues");
  } catch (error) {
    console.warn("[offlineSync] Nie udało się zmigrować pending_issues:", error);
  }
}

export async function syncOfflineQueue(): Promise<number> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return 0;
  if (syncInFlight) return syncInFlight;

  syncInFlight = (async () => {
    await migrateLegacyPendingIssues();
    const mutations = await getAllMutations();
    let synced = 0;

    for (const mutation of mutations) {
      try {
        if (mutation.type === "toggle_task") {
          await applyToggle(mutation.payload as ToggleTaskPayload);
        } else if (mutation.type === "task_note") {
          await applyTaskNote(mutation.payload as TaskNotePayload);
        } else if (mutation.type === "task_photo") {
          if (!mutation.photoBlobId) throw new Error("Brak zdjęcia w kolejce");
          await applyTaskPhoto(mutation.payload as TaskPhotoPayload, mutation.photoBlobId);
        } else if (mutation.type === "create_issue") {
          await applyIssue(mutation.payload as CreateIssuePayload, mutation.photoBlobId);
        }
        await deleteMutation(mutation.id);
        synced += 1;
      } catch (error) {
        console.warn("[offlineSync] Replay failed, zatrzymuję kolejkę:", error);
        await putMutation(mutation);
        break;
      }
    }

    return synced;
  })();

  try {
    return await syncInFlight;
  } finally {
    syncInFlight = null;
  }
}
